import React from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import debounce from "lodash/debounce";
import { api } from "../../../../api/api";

export interface IOrganizationUser {
  id: string;
  displayName: string;
  mail: string;
  givenName?: string;
  surname?: string;
  userPrincipalName?: string;
}

export interface IEmailAutocompleteInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

export function mapPeopleResult(person: any): IOrganizationUser {
  return {
    id: person.id ?? "",
    displayName: person.displayName ?? "",
    mail: person.mail ?? person.scoredEmailAddresses?.[0]?.address ?? "",
    givenName: person.givenName,
    surname: person.surname,
    userPrincipalName: person.userPrincipalName,
  };
}

export const EmailAutocompleteInput: React.FC<IEmailAutocompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  error,
  helperText,
}) => {
  const [searchText, setSearchText] = React.useState("");
  const [allUsers, setAllUsers] = React.useState<IOrganizationUser[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [fetched, setFetched] = React.useState(false);
  const [peopleResults, setPeopleResults] = React.useState<IOrganizationUser[]>([]);
  const [peopleLoading, setPeopleLoading] = React.useState(false);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [popperWidth, setPopperWidth] = React.useState<number | undefined>(undefined);

  const anchorCallbackRef = React.useCallback((node: HTMLDivElement | null) => {
    setAnchorEl(node);
    if (node) {
      setPopperWidth(node.offsetWidth);
    }
  }, []);

  const fetchPeople = (query: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setPeopleLoading(true);

    api
      .get("/outlook/people", {
        params: { search: query },
        signal: controller.signal,
      })
      .then((response) => {
        const data = response.data?.data;
        if (Array.isArray(data) && data.length > 0) {
          setPeopleResults(data.map(mapPeopleResult));
        } else {
          setPeopleResults([]);
        }
        setPeopleLoading(false);
      })
      .catch((error) => {
        if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
          return;
        }
        setPeopleResults([]);
        setPeopleLoading(false);
      });
  };

   
  const debouncedFetchPeople = React.useMemo(() => debounce(fetchPeople, 300), []);


  const handleDelete = (emailToDelete: string) => {
    onChange(value.filter((email) => email !== emailToDelete));
  };

  const handleSelectUser = (user: IOrganizationUser) => {
    onChange([...value, user.mail]);
    setSearchText("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = searchText.trim();
      if (trimmed) {
        onChange([...value, trimmed]);
        setSearchText("");
        setOpen(false);
      }
    } else if (e.key === "Backspace") {
      if (searchText === "" && value.length > 0) {
        onChange(value.slice(0, -1));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const lastChar = inputValue.slice(-1);
    if (lastChar === "," || lastChar === ";") {
      const trimmed = inputValue.slice(0, -1).trim();
      if (trimmed) {
        onChange([...value, trimmed]);
      }
      setSearchText("");
      debouncedFetchPeople.cancel();
      abortControllerRef.current?.abort();
      setPeopleResults([]);
    } else {
      setSearchText(inputValue);
      if (inputValue) {
        debouncedFetchPeople(inputValue);
      } else {
        debouncedFetchPeople.cancel();
        abortControllerRef.current?.abort();
        setPeopleResults([]);
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setOpen(false);
    }, 150);
  };


  // Fetch all organization users once on mount
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .get("/outlook/organization/users")
      .then((response) => {
        if (!cancelled) {
          setAllUsers(response.data.data as IOrganizationUser[]);
          setFetched(true);
        }
      })
      .catch(() => {
        // Silently handle error
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Cleanup debounce and abort on unmount
  React.useEffect(() => {
    return () => {
      debouncedFetchPeople.cancel();
      abortControllerRef.current?.abort();
    };
     
  }, []);

  // Filter users locally based on searchText
  const filteredUsers = React.useMemo(() => {
    if (!searchText.trim()) return [];
    const query = searchText.toLowerCase();
    return allUsers.filter(
      (user) =>
        user.displayName.toLowerCase().includes(query) ||
        user.mail.toLowerCase().includes(query),
    );
  }, [searchText, allUsers]);

  // Prefer People API results; fall back to local filtering
  const displayedUsers = peopleResults.length > 0 ? peopleResults : filteredUsers;

  // Open/close dropdown based on filtered results
  React.useEffect(() => {
    if (searchText.trim().length > 0 && fetched) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [searchText, fetched]);

  // Click-outside handler to close dropdown
  React.useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  return (
    <Box ref={containerRef}>
      <Box
        ref={anchorCallbackRef}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.5,
          border: "1px solid",
          borderColor: error ? "error.main" : "#dee3e9",
          borderRadius: "5px",
          padding: "4px 8px",
          minHeight: 40,
          cursor: "text",
          "&:hover": {
            borderColor: error ? "error.main" : "text.primary",
          },
          "&:focus-within": {
            borderColor: error ? "error.main" : "primary.main",
            borderWidth: 2,
            padding: "3px 7px",
          },
        }}
      >
        {value.map((email) => (
          <Chip
            key={email}
            label={email}
            size="small"
            onDelete={() => handleDelete(email)}
          />
        ))}
        <Box
          component="input"
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : ""}
          sx={{
            flex: 1,
            minWidth: 80,
            border: "none",
            outline: "none",
            fontSize: "0.9rem",
            padding: "4px 0",
            backgroundColor: "transparent",
            color: "rgb(38, 38, 38)",
            "&::placeholder": {
              color: "#767e89",
              opacity: 1,
            },
          }}
        />
        {(loading || peopleLoading) && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
      </Box>

      <Popper
        open={open && (displayedUsers.length > 0 || peopleLoading || (fetched && displayedUsers.length === 0))}
        anchorEl={anchorEl}
        placement="bottom-start"
        placeholder=""
        style={{
          width: popperWidth,
          zIndex: 1300,
        }}
      >
        <Paper
          sx={{
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          {displayedUsers.length > 0
            ? displayedUsers.map((user) => (
              <Box
                key={user.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectUser(user);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 12px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Avatar sx={{ width: 32, height: 32, fontSize: "0.875rem" }}>
                  {user.displayName.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {user.displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.mail}
                  </Typography>
                </Box>
              </Box>
            ))
            : fetched && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ padding: "8px 12px" }}
              >
                No users found
              </Typography>
            )}
        </Paper>
      </Popper>

      {error && helperText && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, ml: 1.5, display: "block" }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};
